/**
 * Modem Model Service
 *
 * Frontend service for managing the curated modem models database.
 * Uses Firebase client SDK directly following esimService.ts pattern.
 *
 * Modem models are global (not org-scoped) - managed by super admins.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { ModemModel, ModemCapabilities } from '@simcheck/shared-types';

const COLLECTION_NAME = 'modem_models';

// Helper to convert Firestore Timestamp or ISO string to Date
function toDate(value: any): Date {
  if (!value) return new Date();
  if (value.toDate && typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value === 'string') {
    return new Date(value);
  }
  if (value instanceof Date) {
    return value;
  }
  return new Date();
}

// ============================================================================
// READ OPERATIONS
// ============================================================================

/**
 * Get all modem models (optionally filtered by active status)
 */
export async function getAllModemModels(activeOnly: boolean = false): Promise<ModemModel[]> {
  let q;

  if (activeOnly) {
    q = query(
      collection(db, COLLECTION_NAME),
      where('isActive', '==', true),
      orderBy('manufacturer', 'asc'),
      orderBy('model', 'asc')
    );
  } else {
    q = query(
      collection(db, COLLECTION_NAME),
      orderBy('manufacturer', 'asc'),
      orderBy('model', 'asc')
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    createdAt: toDate(doc.data().createdAt),
    updatedAt: toDate(doc.data().updatedAt),
  })) as ModemModel[];
}

/**
 * Get a single modem model by ID
 */
export async function getModemModel(modelId: string): Promise<ModemModel | null> {
  const docRef = doc(db, COLLECTION_NAME, modelId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
    createdAt: toDate(docSnap.data().createdAt),
    updatedAt: toDate(docSnap.data().updatedAt),
  } as ModemModel;
}

/**
 * Find modem model by manufacturer and model name
 * Used for auto-populating capabilities on edge device modems
 */
export async function findModemModelByName(
  manufacturer: string,
  model: string
): Promise<ModemModel | null> {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('manufacturer', '==', manufacturer),
    where('model', '==', model),
    where('isActive', '==', true)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docData = snapshot.docs[0];
  return {
    id: docData.id,
    ...docData.data(),
    createdAt: toDate(docData.data().createdAt),
    updatedAt: toDate(docData.data().updatedAt),
  } as ModemModel;
}

/**
 * Search modem models by manufacturer or model name (partial match)
 */
export async function searchModemModels(searchTerm: string): Promise<ModemModel[]> {
  // Firestore doesn't support LIKE queries, so we fetch all and filter client-side
  // For larger datasets, consider using Algolia or a dedicated search service
  const allModels = await getAllModemModels(true);
  const lowerSearch = searchTerm.toLowerCase();

  return allModels.filter(model =>
    model.manufacturer.toLowerCase().includes(lowerSearch) ||
    model.model.toLowerCase().includes(lowerSearch) ||
    (model.description?.toLowerCase().includes(lowerSearch) ?? false)
  );
}

/**
 * Get unique manufacturers from the database
 */
export async function getManufacturers(): Promise<string[]> {
  const allModels = await getAllModemModels(true);
  const manufacturers = [...new Set(allModels.map(m => m.manufacturer))];
  return manufacturers.sort();
}

// ============================================================================
// WRITE OPERATIONS
// ============================================================================

/**
 * Create a new modem model
 */
export async function createModemModel(
  userId: string,
  data: {
    manufacturer: string;
    model: string;
    description?: string;
    capabilities: ModemCapabilities;
    isActive?: boolean;
  }
): Promise<ModemModel> {
  const modelData = {
    manufacturer: data.manufacturer.trim(),
    model: data.model.trim(),
    description: data.description?.trim() || null,
    capabilities: data.capabilities,
    isActive: data.isActive ?? true,
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), modelData);

  return {
    id: docRef.id,
    manufacturer: data.manufacturer.trim(),
    model: data.model.trim(),
    description: data.description?.trim(),
    capabilities: data.capabilities,
    isActive: data.isActive ?? true,
    createdBy: userId,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Update an existing modem model
 */
export async function updateModemModel(
  modelId: string,
  updates: {
    manufacturer?: string;
    model?: string;
    description?: string;
    capabilities?: ModemCapabilities;
    isActive?: boolean;
  }
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, modelId);

  // Verify it exists
  const existing = await getDoc(docRef);
  if (!existing.exists()) {
    throw new Error('Modem model not found');
  }

  const updateData: any = {
    updatedAt: serverTimestamp(),
  };

  if (updates.manufacturer !== undefined) {
    updateData.manufacturer = updates.manufacturer.trim();
  }
  if (updates.model !== undefined) {
    updateData.model = updates.model.trim();
  }
  if (updates.description !== undefined) {
    updateData.description = updates.description?.trim() || null;
  }
  if (updates.capabilities !== undefined) {
    updateData.capabilities = updates.capabilities;
  }
  if (updates.isActive !== undefined) {
    updateData.isActive = updates.isActive;
  }

  await updateDoc(docRef, updateData);
}

/**
 * Delete a modem model
 */
export async function deleteModemModel(modelId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, modelId);

  // Verify it exists
  const existing = await getDoc(docRef);
  if (!existing.exists()) {
    throw new Error('Modem model not found');
  }

  await deleteDoc(docRef);
}

// ============================================================================
// SEED DATA
// ============================================================================

/**
 * Seed the database with initial modem models
 * Call this once to initialize the collection
 */
export async function seedModemModels(userId: string): Promise<number> {
  const { convertLegacyCapabilities } = await import('@simcheck/shared-types');

  const seedData = [
    {
      manufacturer: 'Quectel',
      model: 'EG95',
      description: 'LTE Cat 4 module with VoLTE support',
      capabilities: convertLegacyCapabilities({
        supports5G: false,
        supports4G: true,
        supports3G: true,
        supports2G: true,
        supportsVoLTE: true,
        supportseSIM: false,
        maxDownloadSpeed: 150,
        maxUploadSpeed: 50,
      }),
    },
    {
      manufacturer: 'Quectel',
      model: 'BG95',
      description: 'LTE Cat M1/NB-IoT module for IoT applications',
      capabilities: convertLegacyCapabilities({
        supports5G: false,
        supports4G: true,
        supports3G: false,
        supports2G: false,
        supportsVoLTE: false,
        supportseSIM: false,
        maxDownloadSpeed: 10,
        maxUploadSpeed: 5,
      }),
    },
    {
      manufacturer: 'Sequans',
      model: 'CB610L',
      description: 'LTE-M/NB-IoT module with GPS',
      capabilities: convertLegacyCapabilities({
        supports5G: false,
        supports4G: true,
        supports3G: false,
        supports2G: false,
        supportsVoLTE: false,
        supportseSIM: false,
        maxDownloadSpeed: 10,
        maxUploadSpeed: 5,
      }),
    },
    {
      manufacturer: 'Quectel',
      model: 'EM120R-GL',
      description: 'LTE Cat 4 module - Global version with excellent band support',
      capabilities: convertLegacyCapabilities({
        supports5G: false,
        supports4G: true,
        supports3G: true,
        supports2G: true,
        supportsVoLTE: false,
        supportseSIM: false,
        maxDownloadSpeed: 150,
        maxUploadSpeed: 50,
      }),
    },
  ];

  let created = 0;

  for (const data of seedData) {
    // Check if already exists
    const existing = await findModemModelByName(data.manufacturer, data.model);
    if (!existing) {
      await createModemModel(userId, {
        ...data,
        isActive: true,
      });
      created++;
    }
  }

  return created;
}
